import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { AuthedImage } from "@/components/AuthedImage";
import { TYPOLOGIES } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Library, Plus } from "lucide-react";

export default function DrawingLibrary() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [typology, setTypology] = useState("Fixed");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/library/drawings");
      setItems(data);
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file || !name.trim()) { toast.error("Pick a file and enter a name"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name.trim());
    fd.append("typology", typology);
    try {
      await api.post("/library/drawings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Drawing imported");
      setOpen(false); setFile(null); setName("");
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/library/drawings/${id}`);
      setItems((x) => x.filter((d) => d.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Drawing Library
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Import reference drawings once and reuse them across quotations.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="library-import-button" className="rounded-sm bg-blue-600 hover:bg-blue-700 h-11 px-5">
                <Plus className="w-4 h-4 mr-2" /> Import Drawing
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display">Import Drawing</DialogTitle>
                <DialogDescription>Upload an image or PDF to your shared drawing library.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">File (image or PDF)</Label>
                  <Input type="file" accept="image/*,application/pdf" data-testid="library-file-input"
                    onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); if (f && !name) setName(f.name.replace(/\.[^.]+$/, "")); }}
                    className="rounded-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
                    <Input value={name} data-testid="library-name-input" onChange={(e) => setName(e.target.value)} className="rounded-sm h-9" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Typology</Label>
                    <Select value={typology} onValueChange={setTypology}>
                      <SelectTrigger className="h-9 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPOLOGIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={upload} disabled={uploading} data-testid="library-upload-submit" className="rounded-sm bg-blue-600 hover:bg-blue-700 w-full">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Import
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm py-20 text-center">
            <Library className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No drawings yet. Import your first reference drawing.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="library-grid">
            {items.map((d, i) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden group"
              >
                <div className="h-36 bg-slate-50">
                  <AuthedImage fileId={d.file_id} filename={d.filename} className="w-full h-full object-contain" />
                </div>
                <div className="p-2.5 border-t border-slate-100 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{d.name}</div>
                    <div className="text-[10px] text-slate-400 truncate">{d.typology || "—"}</div>
                  </div>
                  <button
                    data-testid={`library-delete-${d.id}`}
                    onClick={() => remove(d.id)}
                    className="text-slate-400 hover:text-red-600 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
